import { useState, useEffect } from 'react';
import { fromSceneRow, pickLiveVersion, pickNewestDraft, sortNewestFirst } from '@/modules/storyData/versions';
import { CopyIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import { Story } from '@/types/cmsTypes';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { sceneToLegacy } from '@/infrastructure/api/v1/mappers/scenes';
import { storyToLegacy } from '@/infrastructure/api/v1/mappers/stories';
import LureoButton from '@/common/LureoButton';
import { Input } from '@/components/cms/ui/input';
import { Checkbox } from '@/components/cms/ui/checkbox';
import { Choice } from '@/components/ui/choice';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { TagField } from '@/components/ui/tag-field';
import { ERROR_INPUT_CLASS } from '@/components/publish-flow/validation';
import { FieldError, RequiredMark } from '@/components/publish-flow/FieldMarks';
import { FieldLabel, FieldHint } from '@/components/ui/text';

export interface ProjectCopyResult {
  success: boolean;
  // When the copy failed because the named project wasn't found, this is shown
  // inline under the project-name field so the user can correct it and retry.
  projectNameError?: string;
  // Recipients that were not copied. On a partial success the dialog narrows the
  // email pills to these, so retrying can't re-copy the recipients that already
  // succeeded (which would duplicate their projects/stories).
  failedEmails?: string[];
}

interface ProjectCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (targetEmails: string[], selectedSceneInstanceIds: string[], targetProjectName?: string) => Promise<ProjectCopyResult>;
  onCancel: () => void;
  stories: Story[];
  projectName?: string;
  projectId: string;
}

export function ProjectCopyDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  projectName = 'this project',
  projectId,
}: ProjectCopyDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [targetEmails, setTargetEmails] = useState<string[]>([]);
  const [targetProjectName, setTargetProjectName] = useState('');
  const [projectNameError, setProjectNameError] = useState<string | null>(null);
  const [selectedStories, setSelectedStories] = useState<string[]>([]);
  const [storiesWithVersions, setStoriesWithVersions] = useState<any[]>([]);
  const [versionsState, setVersionsState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [readAttempt, setReadAttempt] = useState(0);

  // Fetch all scene instances for this project and group by story
  useEffect(() => {
    if (open && projectId) {
      let cancelled = false;
      const fetchVersions = async () => {
        setVersionsState('loading');
        try {
          const [scenesResult, storiesResult] = await Promise.all([
            listAll(page => v1.GET('/api/v1/scenes', { params: { query: { projectUniqueId: [projectId], ...page } } })),
            listAll(page => v1.GET('/api/v1/stories', { params: { query: { projectUniqueId: [projectId], ...page } } })),
          ]);
          if (cancelled) return;
          if (scenesResult.error || storiesResult.error) {
            console.error('Error fetching versions:', scenesResult.error ?? storiesResult.error);
            setVersionsState('error');
            return;
          }
          const sceneInstances = scenesResult.data.map(sceneToLegacy);
          const sceneOrigins = storiesResult.data.map(storyToLegacy);

          // Group by scene_origin_id (story) and create story objects with their versions
          const groupedByStory = sceneInstances.reduce((acc, version) => {
            const storyId = version.scene_origin_id;
            if (!acc[storyId]) {
              const sceneOrigin = sceneOrigins?.find(so => so.scene_origin_id === storyId);
              acc[storyId] = {
                storyId,
                storyName: sceneOrigin?.scene_origin_name || 'Untitled',
                storyOrder: sceneOrigin?.scene_origin_order_number || 0,
                versions: []
              };
            }
            acc[storyId].versions.push(version);
            return acc;
          }, {});

          // Convert to array and sort by story order
          const storiesArray = Object.values(groupedByStory)
            .sort((a: any, b: any) => a.storyOrder - b.storyOrder)
            .map((story: any) => ({ ...story, versions: sortNewestFirst(story.versions, fromSceneRow) }));

          setStoriesWithVersions(storiesArray);
          setVersionsState('ready');
        } catch (error) {
          console.error('Error fetching versions:', error);
          if (!cancelled) setVersionsState('error');
        }
      };

      fetchVersions();
      return () => {
        cancelled = true;
      };
    }
  }, [open, projectId, readAttempt]);

  const resetForm = () => {
    setTargetEmails([]);
    setTargetProjectName('');
    setProjectNameError(null);
    setSelectedStories([]);
  };

  const handleConfirm = async () => {
    if (selectedStories.length === 0 || targetEmails.length === 0) return;

    setProjectNameError(null);
    setIsLoading(true);
    try {
      const result = await onConfirm(targetEmails, selectedStories, targetProjectName.trim() || undefined);
      // Close only on a clean copy. On failure keep the dialog open so the user
      // can fix the input and retry; a not-found project surfaces inline.
      if (result.success) {
        onOpenChange(false);
        resetForm();
      } else {
        // Drop recipients that already succeeded so a retry only resubmits the
        // ones that failed (otherwise the succeeded copies would be duplicated).
        if (result.failedEmails && result.failedEmails.length > 0) {
          setTargetEmails(result.failedEmails);
        }
        if (result.projectNameError) setProjectNameError(result.projectNameError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Count unique stories being copied
  const getUniqueStoryCount = () => {
    const selectedStoryIds = new Set();
    selectedStories.forEach((versionId) => {
      const story = storiesWithVersions.find((s) => s.versions.some((v) => v.scene_instance_id === versionId));
      if (story) {
        selectedStoryIds.add(story.storyId);
      }
    });
    return selectedStoryIds.size;
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
    onOpenChange(false);
  };

  const handleSelectAll = () => {
    const oneVersionPerStory = storiesWithVersions.map(story => {
      const versions = story.versions as ReturnType<typeof sceneToLegacy>[];
      return (pickLiveVersion(versions, fromSceneRow) ?? pickNewestDraft(versions, fromSceneRow))?.scene_instance_id;
    }).filter(Boolean);

    if (selectedStories.length === oneVersionPerStory.length) {
      setSelectedStories([]);
    } else {
      setSelectedStories(oneVersionPerStory);
    }
  };

  const handleSelectVersion = (versionId: string, checked: boolean) => {
    if (checked) {
      // Find which story this version belongs to
      const story = storiesWithVersions.find(s =>
        s.versions.some(v => v.scene_instance_id === versionId)
      );

      if (story) {
        // Remove any other versions from the same story
        const otherVersionsFromSameStory = story.versions
          .map(v => v.scene_instance_id)
          .filter(id => id !== versionId);

        setSelectedStories(prev => [
          ...prev.filter(id => !otherVersionsFromSameStory.includes(id)),
          versionId
        ]);
      } else {
        setSelectedStories(prev => [...prev, versionId]);
      }
    } else {
      setSelectedStories(prev => prev.filter(id => id !== versionId));
    }
  };

  const emailCount = targetEmails.length;
  const storyCount = getUniqueStoryCount();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent width="lg" scrim="soft" className="bg-lureo-surface">
        <DialogHeader>
          <DialogTitle className="text-lureo-16">Copy Project &amp; Stories to User</DialogTitle>
          <DialogDescription>
            Duplicate '{projectName}' and the stories you pick into another user's account.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* Target emails */}
          <div>
            <FieldLabel mb>
              Target user emails
              <RequiredMark />
            </FieldLabel>
            <TagField
              value={targetEmails}
              onChange={setTargetEmails}
              placeholder="Type or paste emails, then press Enter"
              splitOn={/[\s,;]+/}
            />
            {emailCount > 0 && (
              <FieldHint>{emailCount} recipient{emailCount !== 1 ? 's' : ''}</FieldHint>
            )}
          </div>

          {/* Optional target project name */}
          <div>
            <FieldLabel mb htmlFor="targetProjectName">
              Target project name
              <span className="ml-1.5 normal-case tracking-normal font-lureo-font-ui text-lureo-12 text-lureo-ink-subtle">(optional)</span>
            </FieldLabel>
            <Input
              id="targetProjectName"
              placeholder="Leave blank to create a new project"
              value={targetProjectName}
              onChange={(e) => {
                setTargetProjectName(e.target.value);
                if (projectNameError) setProjectNameError(null);
              }}
              className={projectNameError ? ERROR_INPUT_CLASS : undefined}
            />
            {projectNameError ? (
              <FieldError>{projectNameError}</FieldError>
            ) : (
              <FieldHint>Enter an existing project on the target account to add these stories to it.</FieldHint>
            )}
          </div>

          {/* Story selection */}
          {versionsState === 'error' ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <WarningOctagonIcon className="h-8 w-8 text-lureo-warning-ink" />
              <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">We couldn't load this project's stories.</p>
              <LureoButton variant="outline-2" suppressTooltip className="h-9 px-4 rounded-lureo-radius-button-pill" onClick={() => setReadAttempt((n) => n + 1)}>
                Try again
              </LureoButton>
            </div>
          ) : versionsState === 'loading' && storiesWithVersions.length === 0 ? (
            <div className="py-8 text-center">
              <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">Loading stories...</p>
            </div>
          ) : storiesWithVersions.length === 0 ? (
            <div className="py-8 text-center">
              <WarningOctagonIcon className="mx-auto mb-2 h-8 w-8 text-lureo-warning-ink" />
              <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">No versions available to copy.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <FieldLabel>Stories</FieldLabel>

              <Choice
                className="gap-3 rounded-lureo-radius-lg border-lureo-border-width border-lureo-line bg-lureo-brand-ink/[0.06] px-4 py-3"
                control={
                  <Checkbox
                    id="select-all"
                    checked={selectedStories.length === storiesWithVersions.length}
                    onCheckedChange={handleSelectAll}
                  />
                }
                label={`Select all (${storiesWithVersions.length})`}
              />

              <div className="flex flex-col gap-4">
                {storiesWithVersions.map((story) => (
                  <div key={story.storyId} className="flex flex-col gap-1.5">
                    <div className="font-lureo-font-ui font-semibold text-lureo-13 text-lureo-ink leading-[1.3] border-b-lureo-border-width border-lureo-line pb-1">
                      {story.storyName}
                    </div>
                    {story.versions.map((version) => {
                      const isSelected = selectedStories.includes(version.scene_instance_id);
                      const isDraft = version.scene_instance_status === 'draft' || version.scene_instance_status === 'template';
                      return (
                        <Choice
                          key={version.scene_instance_id}
                          className={`items-start gap-3 rounded-lureo-radius-lg px-3 py-2 transition-colors ${isSelected ? 'bg-lureo-brand-ink/10 border-lureo-border-width-medium border-lureo-brand-ink' : 'border-lureo-border-width border-transparent hover:border-lureo-line'}`}
                          control={
                            <Checkbox
                              id={version.scene_instance_id}
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                handleSelectVersion(version.scene_instance_id, checked as boolean)
                              }
                            />
                          }
                          label={
                            <span className="flex items-center gap-2">
                              <span className="font-semibold tabular-nums">v{version.scene_instance_version_number}</span>
                              {version.scene_instance_status === 'live' && <StatusBadge status="live" />}
                              {isDraft && <StatusBadge status="draft" />}
                            </span>
                          }
                          description={<span className="tabular-nums">Last updated: {new Date(version.updated_at).toLocaleDateString()}</span>}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <LureoButton
            variant="outline-2"
            suppressTooltip
            onClick={handleCancel}
            className="h-9 px-5 rounded-lureo-radius-button-pill"
            disabled={isLoading}
          >
            Cancel
          </LureoButton>
          <LureoButton
            variant="secondary"
            suppressTooltip
            onClick={handleConfirm}
            className="h-9 px-4 rounded-lureo-radius-button-pill"
            disabled={isLoading || selectedStories.length === 0 || targetEmails.length === 0}>
            {isLoading ? (
              'Copying…'
            ) : (
              <>
                <CopyIcon className="h-4 w-4 shrink-0" />
                {`Copy ${storyCount} ${storyCount !== 1 ? 'stories' : 'story'}`}
              </>
            )}
          </LureoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
